import React from "react";
import { Box } from "Components/Base/Layout/Box";
import TextLikeField from "lib/components/ItemMetadataFields/fields/TextLikeField";
import { getMetadataFieldComponent } from "lib/components/ItemMetadataFields/fields/fieldComponentByType";

/**
 * Renders configurable metadata fields (same shapes as legacy admin metadata).
 * Field labels use {@code languageContext(field.name)} — configure phrase keys in Metadata admin.
 *
 * @param {Object} props
 * @param {Array<{ id: number, fieldType: number, name: string, obligatory: boolean, options?: string[] }>} props.fields
 * @param {Record<number, string>} props.valuesById
 * @param {(id: number, value: string) => void} props.onChange
 * @param {boolean} [props.disabled=false]
 * @param {(key: string) => string} props.languageContext
 * @param {string} [props.sectionTitleKey="externalCourseMetadataSection"] Phrase key for the section’s accessible name (no visible title).
 * @param {Record<number, string>} [props.errorsByFieldId] Inline validation messages keyed by field id
 */
const ItemMetadataFields = ({
  fields = [],
  valuesById = {},
  onChange,
  disabled = false,
  languageContext,
  sectionTitleKey = "externalCourseMetadataSection",
  errorsByFieldId = {},
}) => {
  if (!fields.length) {
    return null;
  }

  return (
    <Box
      component="section"
      aria-label={languageContext(sectionTitleKey)}
      mb={1.25}
    >
      {fields.map((field) => {
        const metadataFieldState = {
          fieldError: errorsByFieldId[field.id] || "",
          fieldId: `item-metadata-${field.id}`,
          labelText: languageContext(field.name),
          required: Boolean(field.obligatory),
          value: valuesById[field.id] ?? "",
        };
        const FieldComponent =
          getMetadataFieldComponent(field.fieldType) || TextLikeField;

        return (
          <FieldComponent
            key={field.id}
            field={field}
            fieldState={metadataFieldState}
            disabled={disabled}
            onChange={onChange}
          />
        );
      })}
    </Box>
  );
};

export default ItemMetadataFields;
